import './styles/main.css';
import logoUrl from '../logo.png'; // 👈 Fixes Vite asset bundling
import { getState, subscribe, setView } from './state/store.js';
import { getCurrentUser, logout } from './api/auth.js';
import { renderAuthView } from './components/auth-view.js';
import { renderCalendarView } from './components/calendar-view.js';
import { renderBookingForm } from './components/booking-form.js';

// --- THEME STATE ENGINE ---
function getSavedTheme() {
  return localStorage.getItem('mm_theme') || 'dark';
}

function applyTheme(theme) {
  if (theme === 'light') {
    document.body.classList.add('light-mode');
  } else {
    document.body.classList.remove('light-mode');
  }
  localStorage.setItem('mm_theme', theme);
}

// Initialize theme on script load
applyTheme(getSavedTheme());

// --- GLOBAL DIAGNOSTIC ERROR BOUNDARY ---
window.addEventListener('error', (e) => {
  const appRoot = document.getElementById('app') || document.body;
  appRoot.innerHTML = `
    <div style="padding: 24px; background: #0f172a; color: #f87171; font-family: monospace; font-size: 12px; border-radius: 12px; margin: 20px; border: 1px solid #ef4444;">
      <h3 style="font-weight: bold; margin-bottom: 8px;">🚨 Portal Execution Exception</h3>
      <p style="margin-bottom: 12px;">${e.message} at ${e.filename}:${e.lineno}</p>
      <button onclick="localStorage.clear(); location.reload();" style="padding: 6px 12px; background: #ef4444; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-weight: bold;">
        Clear Local Cache & Reload
      </button>
    </div>
  `;
});

let lastRenderedView = null;

function initializeAppShell() {
  const appRoot = document.getElementById('app') || document.body;
  const user = getCurrentUser();

  if (!user) {
    appRoot.innerHTML = `<div id="view-container" class="max-w-md mx-auto pt-10 p-4"></div>`;
    renderAuthView(document.getElementById('view-container'), {
      onSuccess: () => initializeAppShell()
    });
    return;
  }

  const clientName = user.agencyName || user.companyName || user.authEmail || 'Operator';
  const currentTheme = getSavedTheme();

  appRoot.innerHTML = `
    <div class="max-w-6xl mx-auto p-3 sm:p-6 space-y-6">
      <header class="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-800">
        <!-- BRAND LOGO & CLIENT IDENTITY BLOCK -->
        <div class="flex items-center gap-3.5">
          <img 
            src="${logoUrl}" 
            alt="Major Maker Logo" 
            class="w-11 h-11 rounded-full border border-slate-700/80 bg-slate-900 object-contain p-0.5 shadow-lg flex-shrink-0" 
          />
          <div>
            <h1 class="text-xl sm:text-2xl font-black tracking-tight text-white flex items-center gap-2">
              <span>Major Maker</span>
              <span class="text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full uppercase tracking-wider">
                Live Sync
              </span>
            </h1>
            <p class="text-xs text-slate-400 font-medium mt-0.5">${clientName} • Bookings Manager</p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <!-- NAVIGATION TABS -->
          <div class="flex items-center bg-slate-900 p-1 rounded-xl border border-slate-800">
            <button id="nav-calendar" class="px-3.5 py-1.5 text-xs font-semibold rounded-lg text-slate-400 hover:text-white transition">📅 Calendar</button>
            <button id="nav-list" class="px-3.5 py-1.5 text-xs font-semibold rounded-lg text-slate-400 hover:text-white transition">📋 List</button>
            <button id="nav-booking" class="px-3.5 py-1.5 text-xs font-semibold rounded-lg text-slate-400 hover:text-white transition">➕ New Booking</button>
          </div>

          <!-- LIGHT / DARK THEME TOGGLE BUTTON -->
          <button id="theme-toggle-btn" title="Toggle Light / Dark Mode" class="p-2.5 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded-xl border border-slate-800 text-xs font-semibold transition">
            ${currentTheme === 'light' ? '🌙' : '☀️'}
          </button>

          <!-- LOGOUT BUTTON -->
          <button id="logout-btn" title="Lock Session / Log Out" class="p-2.5 bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-red-400 rounded-xl border border-slate-800 text-xs font-semibold transition">
            🔒
          </button>
        </div>
      </header>

      <main id="view-container"></main>
    </div>
  `;

  // Bind Navigation
  document.getElementById('nav-calendar').addEventListener('click', () => setView('calendar'));
  document.getElementById('nav-list').addEventListener('click', () => setView('list'));
  document.getElementById('nav-booking').addEventListener('click', () => setView('booking'));

  // Bind Theme Switcher
  document.getElementById('theme-toggle-btn').addEventListener('click', () => {
    const newTheme = getSavedTheme() === 'light' ? 'dark' : 'light';
    applyTheme(newTheme);
    initializeAppShell();
  });

  // Bind Logout
  document.getElementById('logout-btn').addEventListener('click', () => {
    logout();
    location.reload();
  });
}

function renderActiveView(state) {
  const viewContainer = document.getElementById('view-container');
  if (!viewContainer || !getCurrentUser()) return;

  const activeView = (state.currentView === 'auth' || state.currentView === 'catalog') 
    ? 'calendar' 
    : state.currentView;

  updateNavTabStyles(activeView);

  if (activeView !== lastRenderedView) {
    lastRenderedView = activeView;
    if (activeView === 'calendar' || activeView === 'list') {
      renderCalendarView(viewContainer, { initialSubView: activeView });
    } else if (activeView === 'booking') {
      renderBookingForm(viewContainer, {
        onSuccess: () => setView('calendar')
      });
    }
  }
}

function updateNavTabStyles(currentView) {
  const btnCal = document.getElementById('nav-calendar');
  const btnList = document.getElementById('nav-list');
  const btnBook = document.getElementById('nav-booking');

  if (!btnCal || !btnList || !btnBook) return;

  const activeClass = "px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white transition";
  const inactiveClass = "px-3.5 py-1.5 text-xs font-semibold rounded-lg text-slate-400 hover:text-white transition";

  btnCal.className = currentView === 'calendar' ? activeClass : inactiveClass;
  btnList.className = currentView === 'list' ? activeClass : inactiveClass;
  btnBook.className = currentView === 'booking' ? activeClass : inactiveClass;
}

function boot() {
  initializeAppShell();
  subscribe((state) => renderActiveView(state));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}